import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    InputBase,
    Link,
    LinearProgress,
    CircularProgress,
    Menu,
    MenuItem,
    Paper,
    Popper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    TextField,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import FolderIcon from '@mui/icons-material/Folder';
import ViewListIcon from '@mui/icons-material/ViewList';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import kbaseService from '../services/KbaseService';
import documentService from '../services/DocumentService';
import ChunkListDialog from './ChunkListDialog';

function normalizeEpochToMs(v) {
    if (v == null) return null;
    const n = Number(v);
    if (!Number.isFinite(n) || n <= 0) return null;
    // If seconds, convert to ms
    return n < 1e12 ? n * 1000 : n;
}

function formatBytes(bytes) {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let idx = 0;
    let v = n;
    while (v >= 1024 && idx < units.length - 1) {
        v /= 1024;
        idx += 1;
    }
    const digits = idx === 0 ? 0 : v < 10 ? 2 : 1;
    return `${v.toFixed(digits)} ${units[idx]}`;
}

function normalizeString(value) {
    return String(value ?? '').trim();
}

const MOVE_ROOT_VALUE = '__MOVE_TO_ROOT__';

// Shared height so the search field, refresh button, and action buttons align.
const TOOLBAR_CONTROL_HEIGHT = 40;

function inferFilenameFromUrl(urlString) {
    const raw = normalizeString(urlString);
    if (!raw) return '';

    try {
        const u = new URL(raw);
        const last = (u.pathname || '').split('/').filter(Boolean).pop() || '';
        return decodeURIComponent(last) || '';
    } catch {
        // Fallback: best-effort parse without URL constructor
        const noQuery = raw.split('?')[0];
        const last = noQuery.split('/').filter(Boolean).pop() || '';
        return last;
    }
}

function getUrlLines(value) {
    return String(value ?? '')
        .split(/\s+/)
        .map((url) => url.trim())
        .filter(Boolean);
}

function isValidUrl(value) {
    try {
        const u = new URL(value);
        return Boolean(u.protocol && u.host);
    } catch {
        return false;
    }
}

function formatUrlForDisplay(value) {
    const url = normalizeString(value);
    if (!url) return '';
    try {
        return decodeURI(url);
    } catch {
        return url;
    }
}

function isPdfDocument(row) {
    if (normalizeString(row?.fileType).toLowerCase() === 'pdf') return true;

    const value = normalizeString(row?.url || row?.filename || row?.name);
    if (!value) return false;
    try {
        return decodeURIComponent(new URL(value).pathname).toLowerCase().endsWith('.pdf');
    } catch {
        const path = value.split(/[?#]/, 1)[0];
        try {
            return decodeURIComponent(path).toLowerCase().endsWith('.pdf');
        } catch {
            return path.toLowerCase().endsWith('.pdf');
        }
    }
}

function isPollingStatus(value) {
    const status = normalizeString(value).toLowerCase();
    return status === 'created'
        || status === 'uploaded'
        || status === 'queued'
        || status === 'processing'
        || status === 'preview-uploading'
        || status === 'deleting';
}

function getDocumentDisplayName(row) {
    const documentType = normalizeString(row?.documentType).toLowerCase();
    if (documentType === 'url') return formatUrlForDisplay(row?.url || row?.filename || row?.name) || '-';
    if (documentType === 'folder') return row?.folderName || row?.filename || row?.name || '-';
    return row?.filename || row?.name || '-';
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const BRAND = '#287bd6';
const BRAND_DARK = '#1f6fca';
const BRAND_GRADIENT = 'linear-gradient(135deg, #287bd6 0%, #1f6fca 100%)';

const dialogPaperSx = {
    borderRadius: '16px',
    boxShadow: '0 24px 60px rgba(16,36,64,0.22)',
    overflow: 'hidden',
};

const primaryBtnSx = {
    background: BRAND_GRADIENT,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.9rem',
    borderRadius: '9px',
    minHeight: 42,
    px: 2.6,
    boxShadow: '0 2px 8px rgba(40,123,214,0.32)',
    '&:hover': {
        background: 'linear-gradient(135deg, #3f8fe0 0%, #287bd6 100%)',
        boxShadow: '0 3px 12px rgba(40,123,214,0.4)',
    },
    '&.Mui-disabled': {
        background: '#c9d6e5',
        color: '#fff',
        boxShadow: 'none',
    },
};

const cancelBtnSx = {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    borderRadius: '9px',
    minHeight: 42,
    px: 2.2,
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
};

function fileIconFor(fileName = '') {
    const ext = String(fileName).split('.').pop().toLowerCase();
    const common = { fontSize: 24 };
    if (ext === 'pdf') return <PictureAsPdfOutlinedIcon sx={{ ...common, color: '#e0554e' }} />;
    if (ext === 'docx' || ext === 'doc') return <DescriptionOutlinedIcon sx={{ ...common, color: BRAND }} />;
    if (ext === 'txt') return <ArticleOutlinedIcon sx={{ ...common, color: '#6b7684' }} />;
    return <InsertDriveFileOutlinedIcon sx={{ ...common, color: '#6b7684' }} />;
}

// Reusable header for the redesigned modals: icon badge + title + subtitle + close.
function ModalHeader({ icon, title, subtitle, onClose, disabled }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, px: 3, pt: 2.75, pb: 2 }}>
            <Box
                aria-hidden
                sx={{
                    flexShrink: 0,
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(40,123,214,0.1)',
                    color: BRAND,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.12rem', color: '#1a2634', lineHeight: 1.3 }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography sx={{ mt: 0.25, fontSize: '0.85rem', color: 'text.secondary' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <IconButton onClick={onClose} size="small" aria-label="close" disabled={disabled} sx={{ mt: -0.5, mr: -0.75, color: 'text.secondary' }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
}

function KbaseDocuments({ kbaseId: kbaseIdOverride = '', embedded = false, toolbarStart = null, onCountChange = null }) {
    const { t } = useTranslation();

    // Translate enum-like column values (documentType / refreshRate / status).
    // Falls back to the raw value when the enum is unrecognized.
    const typeLabel = (value) => {
        const key = normalizeString(value).toLowerCase();
        if (!key) return '-';
        const map = {
            url: t('documents.typeLabels.url', 'URL'),
            file: t('documents.typeLabels.file', 'File'),
            folder: t('documents.typeLabels.folder', 'Folder'),
            website: t('documents.typeLabels.website', 'Website'),
        };
        return map[key] || value;
    };
    const refreshLabel = (value) => {
        const key = normalizeString(value).toLowerCase();
        const map = {
            never: t('documents.refreshNever', 'Never'),
            none: t('documents.refreshNever', 'Never'),
            daily: t('documents.refreshDaily', 'Daily'),
            weekly: t('documents.refreshWeekly', 'Weekly'),
            monthly: t('documents.refreshMonthly', 'Monthly'),
        };
        return map[key] || value;
    };
    const statusLabel = (value) => {
        const key = normalizeString(value).toLowerCase();
        if (!key) return '-';
        const map = {
            initialized: t('documents.statusLabels.initialized', 'Initialized'),
            queued: t('documents.statusLabels.queued', 'Queued'),
            created: t('documents.statusLabels.created', 'Created'),
            processing: t('documents.statusLabels.processing', 'Processing'),
            'preview-uploading': t('documents.statusLabels.previewUploading', 'Finalizing preview…'),
            processed: t('documents.statusLabels.processed', 'Processed'),
            success: t('documents.statusLabels.success', 'Success'),
            deleting: t('documents.statusLabels.deleting', 'Deleting…'),
            failed: t('documents.statusLabels.failed', 'Failed'),
        };
        return map[key] || value;
    };

    const navigate = useNavigate();
    const { kbaseId: routeKbaseId, folderName: folderNameParam } = useParams();
    const kbaseId = normalizeString(kbaseIdOverride) || routeKbaseId;
    const location = useLocation();
    const folderQueryParam = new URLSearchParams(location.search).get('folder');

    const kbaseNameFromNav = normalizeString(location?.state?.kbaseName);
    const [kbaseName, setKbaseName] = useState(kbaseNameFromNav);
    const [currentFolder, setCurrentFolder] = useState('');
    const [currentFolderDocumentType, setCurrentFolderDocumentType] = useState('');
    const [currentFolderDocumentStatus, setCurrentFolderDocumentStatus] = useState('');
    const [currentFolderMaxUrls, setCurrentFolderMaxUrls] = useState(0);

    const [documents, setDocuments] = useState([]);
    const [documentNameSearch, setDocumentNameSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);
    const [createDraft, setCreateDraft] = useState({ documentType: 'File', filename: '', url: '', folderName: '', crawlDepth: 10, maxUrls: 200, refreshRate: 'None', ocrRequired: false });
    const [createFile, setCreateFile] = useState(null);
    const [createUploading, setCreateUploading] = useState(false);
    const [createUploadProgress, setCreateUploadProgress] = useState(0);
    const [urlRows, setUrlRows] = useState([{ id: 0, value: '' }]);
    const nextUrlRowId = useRef(1);
    const urlRowRefs = useRef({});

    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [createFolderName, setCreateFolderName] = useState('');

    const [chunksDialogOpen, setChunksDialogOpen] = useState(false);
    const [selectedChunkDocument, setSelectedChunkDocument] = useState(null);
    const [urlPreview, setUrlPreview] = useState({ anchorEl: null, url: '', imageUrl: '', loading: false, error: false });
    const [pdfViewer, setPdfViewer] = useState({ open: false, filename: '', src: '', loading: false, error: false });
    const pdfViewerRequestRef = useRef(0);
    const pdfViewerSrcRef = useRef('');
    const urlPreviewCacheRef = useRef(new Map());
    const urlPreviewPromiseRef = useRef(new Map());
    const urlPreviewFailureCacheRef = useRef(new Map());
    const urlPreviewRequestRef = useRef(0);

    const [refreshDialogOpen, setRefreshDialogOpen] = useState(false);
    const [refreshDialogDocument, setRefreshDialogDocument] = useState(null);
    const [refreshDialogValue, setRefreshDialogValue] = useState('never');
    const [updatingRefresh, setUpdatingRefresh] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveDialogDocument, setMoveDialogDocument] = useState(null);
    const [moveDialogFolder, setMoveDialogFolder] = useState('');
    const [moveFolderOptions, setMoveFolderOptions] = useState([]);
    const [movingDocument, setMovingDocument] = useState(false);

    const allRows = useMemo(() => (Array.isArray(documents) ? documents : []), [documents]);

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const openChunks = (row) => {
        setSelectedChunkDocument(row || null);
        setChunksDialogOpen(true);
    };

    const closeChunks = () => {
        setChunksDialogOpen(false);
        setSelectedChunkDocument(null);
    };

    const openPdfViewer = async (row) => {
        const documentId = normalizeString(row?.documentId);
        if (!documentId) return;
        const requestId = ++pdfViewerRequestRef.current;
        setPdfViewer({ open: true, filename: getDocumentDisplayName(row), src: '', loading: true, error: false });
        try {
            const src = await kbaseService.getKbasePdfDocumentSrc(kbaseId, documentId);
            if (pdfViewerRequestRef.current !== requestId) {
                URL.revokeObjectURL(src);
                return;
            }
            pdfViewerSrcRef.current = src;
            setPdfViewer((current) => ({ ...current, src, loading: false }));
        } catch (error) {
            if (pdfViewerRequestRef.current !== requestId) return;
            console.error('Failed to load PDF document', error);
            setPdfViewer((current) => ({ ...current, loading: false, error: true }));
        }
    };

    const closePdfViewer = () => {
        pdfViewerRequestRef.current += 1;
        if (pdfViewerSrcRef.current) URL.revokeObjectURL(pdfViewerSrcRef.current);
        pdfViewerSrcRef.current = '';
        setPdfViewer({ open: false, filename: '', src: '', loading: false, error: false });
    };

    useEffect(() => () => {
        if (pdfViewerSrcRef.current) URL.revokeObjectURL(pdfViewerSrcRef.current);
    }, []);

    const fetchUrlPreview = useCallback(async (row) => {
        const url = normalizeString(row?.url);
        const cacheKey = url;
        const cached = urlPreviewCacheRef.current.get(cacheKey);
        if (cached) return cached;
        if (Date.now() - (urlPreviewFailureCacheRef.current.get(cacheKey) || 0) < 60_000) {
            throw new Error('Preview is temporarily unavailable');
        }
        const pending = urlPreviewPromiseRef.current.get(cacheKey);
        if (pending) return pending;

        const request = documentService.getUrlPreview(url)
            .then((previewBlob) => {
                if (!(previewBlob instanceof Blob) || previewBlob.size === 0) {
                    throw new Error('Preview image is empty');
                }
                const imageUrl = URL.createObjectURL(previewBlob);
                urlPreviewCacheRef.current.set(cacheKey, imageUrl);
                urlPreviewFailureCacheRef.current.delete(cacheKey);
                // Start downloading while idle so the hover card can paint immediately.
                const image = new Image();
                image.src = imageUrl;
                return imageUrl;
            })
            .catch((error) => {
                urlPreviewFailureCacheRef.current.set(cacheKey, Date.now());
                throw error;
            })
            .finally(() => urlPreviewPromiseRef.current.delete(cacheKey));
        urlPreviewPromiseRef.current.set(cacheKey, request);
        return request;
    }, []);

    const fetchPdfPreview = useCallback(async (row) => {
        const documentId = normalizeString(row?.documentId);
        if (!documentId) throw new Error('documentId is required');
        const cacheKey = `pdf:${documentId}`;
        const cached = urlPreviewCacheRef.current.get(cacheKey);
        if (cached) return cached;
        const pending = urlPreviewPromiseRef.current.get(cacheKey);
        if (pending) return pending;

        const request = kbaseService.getKbaseDocumentPageImageSrc(kbaseId, documentId, 1)
            .then(async (preview) => {
                const imageUrl = normalizeString(preview?.src);
                if (!imageUrl) throw new Error('Preview image is missing');
                const image = new Image();
                image.src = imageUrl;
                try {
                    await image.decode();
                } catch (error) {
                    URL.revokeObjectURL(imageUrl);
                    throw new Error('Preview image could not be decoded', { cause: error });
                }
                urlPreviewCacheRef.current.set(cacheKey, imageUrl);
                urlPreviewFailureCacheRef.current.delete(cacheKey);
                return imageUrl;
            })
            .finally(() => urlPreviewPromiseRef.current.delete(cacheKey));
        urlPreviewPromiseRef.current.set(cacheKey, request);
        return request;
    }, [kbaseId]);

    const fetchRowPreview = useCallback((row) => {
        return normalizeString(row?.url) ? fetchUrlPreview(row) : fetchPdfPreview(row);
    }, [fetchPdfPreview, fetchUrlPreview]);

    useEffect(() => () => {
        urlPreviewCacheRef.current.forEach((imageUrl) => URL.revokeObjectURL(imageUrl));
        urlPreviewCacheRef.current.clear();
        urlPreviewFailureCacheRef.current.clear();
    }, []);

    const openUrlPreview = async (event, row) => {
        const targetUrl = normalizeString(row?.url);
        const documentId = normalizeString(row?.documentId);
        if (!targetUrl && !documentId) return;
        const previewLabel = targetUrl || getDocumentDisplayName(row);
        const anchorEl = event.currentTarget;
        const cacheKey = targetUrl || `pdf:${documentId}`;
        const cached = urlPreviewCacheRef.current.get(cacheKey);
        if (cached) {
            setUrlPreview({ anchorEl, url: previewLabel, imageUrl: cached, loading: false, error: false });
            return;
        }

        const requestId = ++urlPreviewRequestRef.current;
        setUrlPreview({ anchorEl, url: previewLabel, imageUrl: '', loading: true, error: false });
        try {
            const imageUrl = await fetchRowPreview(row);
            if (urlPreviewRequestRef.current !== requestId) return;
            setUrlPreview({ anchorEl, url: previewLabel, imageUrl, loading: false, error: false });
        } catch (error) {
            if (urlPreviewRequestRef.current !== requestId) return;
            console.error('Failed to load URL preview', error);
            setUrlPreview({ anchorEl, url: previewLabel, imageUrl: '', loading: false, error: true });
        }
    };

    const closeUrlPreview = () => {
        urlPreviewRequestRef.current += 1;
        setUrlPreview({ anchorEl: null, url: '', imageUrl: '', loading: false, error: false });
    };

    const openRefreshDialog = (row) => {
        setRefreshDialogDocument(row || null);
        setRefreshDialogValue(normalizeString(row?.refreshRate).toLowerCase() || 'never');
        setRefreshDialogOpen(true);
    };

    const closeRefreshDialog = () => {
        if (updatingRefresh) return;
        setRefreshDialogOpen(false);
        setRefreshDialogDocument(null);
        setRefreshDialogValue('never');
    };

    const openDelete = (row) => {
        setDeleteTarget(row || null);
        setConfirmDeleteOpen(true);
    };

    const closeDelete = () => {
        setConfirmDeleteOpen(false);
        setDeleteTarget(null);
    };

    const openCreateMenu = (event) => {
        if (normalizeString(currentFolderDocumentType).toLowerCase() === 'website') return;
        setCreateMenuAnchorEl(event.currentTarget);
    };

    const closeCreateMenu = () => {
        setCreateMenuAnchorEl(null);
    };

    const openCreate = ({ documentType = 'File', ocrRequired = false } = {}) => {
        setCreateDraft({ documentType, filename: '', url: '', folderName: '', crawlDepth: 10, maxUrls: 200, refreshRate: 'None', ocrRequired });
        setCreateFile(null);
        setCreateUploading(false);
        setCreateUploadProgress(0);
        setUrlRows([{ id: 0, value: '' }]);
        nextUrlRowId.current = 1;
        urlRowRefs.current = {};
        closeCreateMenu();
        setCreateOpen(true);
    };

    const closeCreate = () => setCreateOpen(false);

    // ---- Excel-like URL grid helpers ----
    // Keep createDraft.url synced with the rows so all existing logic (canCreate,
    // handleCreate, validation memos) keeps working off the same string.
    const commitUrlRows = useCallback((next) => {
        setUrlRows(next);
        setCreateDraft((prev) => ({
            ...prev,
            url: next.map((r) => r.value.trim()).filter(Boolean).join('\n'),
        }));
    }, []);

    const makeUrlRow = useCallback((value = '') => {
        const id = nextUrlRowId.current;
        nextUrlRowId.current += 1;
        return { id, value };
    }, []);

    const focusUrlRow = useCallback((id) => {
        window.requestAnimationFrame(() => {
            const el = urlRowRefs.current[id];
            if (el) {
                el.focus();
                const len = el.value.length;
                try { el.setSelectionRange(len, len); } catch { /* noop */ }
            }
        });
    }, []);

    const handleUrlRowChange = useCallback((id, value) => {
        commitUrlRows(urlRows.map((r) => (r.id === id ? { ...r, value } : r)));
    }, [commitUrlRows, urlRows]);

    const handleUrlRowAdd = useCallback((afterId = null) => {
        const row = makeUrlRow('');
        if (afterId == null) {
            commitUrlRows([...urlRows, row]);
        } else {
            const idx = urlRows.findIndex((r) => r.id === afterId);
            const next = [...urlRows];
            next.splice(idx + 1, 0, row);
            commitUrlRows(next);
        }
        focusUrlRow(row.id);
    }, [commitUrlRows, focusUrlRow, makeUrlRow, urlRows]);

    const handleUrlRowRemove = useCallback((id) => {
        const idx = urlRows.findIndex((r) => r.id === id);
        let next = urlRows.filter((r) => r.id !== id);
        if (next.length === 0) next = [makeUrlRow('')];
        commitUrlRows(next);
        const focusTarget = next[Math.max(0, idx - 1)];
        if (focusTarget) focusUrlRow(focusTarget.id);
    }, [commitUrlRows, focusUrlRow, makeUrlRow, urlRows]);

    const handleUrlRowKeyDown = useCallback((id, e) => {
        const row = urlRows.find((r) => r.id === id);
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleUrlRowAdd(id);
        } else if (e.key === 'Backspace' && row && row.value === '' && urlRows.length > 1) {
            e.preventDefault();
            handleUrlRowRemove(id);
        }
    }, [handleUrlRowAdd, handleUrlRowRemove, urlRows]);

    // Splitting a multi-line / whitespace-separated paste into one row per URL.
    const handleUrlRowPaste = useCallback((id, e) => {
        const text = e.clipboardData?.getData('text') ?? '';
        const parts = text.split(/\s+/).map((s) => s.trim()).filter(Boolean);
        if (parts.length <= 1) return; // let the browser handle a single-token paste
        e.preventDefault();
        const idx = urlRows.findIndex((r) => r.id === id);
        const current = urlRows[idx];
        const next = [...urlRows];
        let lastId;
        if (current && current.value.trim() === '') {
            next[idx] = { ...current, value: parts[0] };
            lastId = current.id;
            const inserted = parts.slice(1).map((v) => makeUrlRow(v));
            next.splice(idx + 1, 0, ...inserted);
            if (inserted.length) lastId = inserted[inserted.length - 1].id;
        } else {
            const inserted = parts.map((v) => makeUrlRow(v));
            next.splice(idx + 1, 0, ...inserted);
            lastId = inserted[inserted.length - 1].id;
        }
        commitUrlRows(next);
        focusUrlRow(lastId);
    }, [commitUrlRows, focusUrlRow, makeUrlRow, urlRows]);

    const openCreateFolder = () => {
        setCreateFolderName('');
        setCreateFolderOpen(true);
    };

    const closeCreateFolder = () => setCreateFolderOpen(false);

    const loadMoveFolders = useCallback(async () => {
        if (!kbaseId) return [];

        const rootDocuments = normalizeString(currentFolder)
            ? await kbaseService.getKbaseDocuments(kbaseId)
            : allRows;
        const folders = (Array.isArray(rootDocuments) ? rootDocuments : [])
            .filter((row) => normalizeString(row?.documentType).toLowerCase() === 'folder')
            .map((row) => normalizeString(row?.folderName || row?.filename || row?.name))
            .filter(Boolean);

        return Array.from(new Set(folders)).sort((a, b) => a.localeCompare(b));
    }, [allRows, currentFolder, kbaseId]);

    const openMoveDialog = async (row) => {
        const sourceFolder = normalizeString(row?.folder) || normalizeString(currentFolder);
        setMoveDialogDocument(row || null);
        setMoveDialogOpen(true);
        setMoveFolderOptions([]);
        setMoveDialogFolder('');

        try {
            const folders = await loadMoveFolders();
            const destinationFolders = folders.filter((folder) => folder !== sourceFolder);
            setMoveFolderOptions(destinationFolders);
            setMoveDialogFolder(sourceFolder ? MOVE_ROOT_VALUE : (destinationFolders[0] || ''));
        } catch (e) {
            console.error('Error loading folders for move', e);
            setSnackbar({ open: true, message: e?.message || t('documents.moveFolderLoadError', 'Failed to load folders'), severity: 'error' });
        }
    };

    const closeMoveDialog = () => {
        if (movingDocument) return;
        setMoveDialogOpen(false);
        setMoveDialogDocument(null);
        setMoveDialogFolder('');
        setMoveFolderOptions([]);
    };

    const fetchDocuments = useCallback(async (options = {}) => {
        if (!kbaseId) return;
        const silent = Boolean(options?.silent);
        try {
            if (!silent) setLoading(true);
            const data = await kbaseService.getKbaseDocuments(kbaseId, {
                folder: normalizeString(currentFolder) || undefined,
            });
            const nextDocuments = Array.isArray(data) ? data : [];
            setDocuments(nextDocuments);
            onCountChange?.(nextDocuments.length);
        } catch (e) {
            console.error('Error fetching kbase documents', e);
            setSnackbar({
                open: true,
                message: e?.message || t('documents.fetchError', 'Failed to load documents'),
                severity: 'error',
            });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [kbaseId, t, currentFolder, onCountChange]);

    const fetchKbaseName = useCallback(async () => {
        if (!kbaseId) return;
        if (kbaseNameFromNav) {
            setKbaseName(kbaseNameFromNav);
            return;
        }

        try {
            const list = await kbaseService.getCustomerKbases();
            const match = Array.isArray(list) ? list.find((k) => String(k?.kbaseId) === String(kbaseId)) : null;
            const name = normalizeString(match?.kbaseName);
            if (name) setKbaseName(name);
        } catch (e) {
            // Non-blocking; fallback to showing id.
            console.warn('Failed to load kbase name', e);
        }
    }, [kbaseId, kbaseNameFromNav]);

    useEffect(() => {
        fetchDocuments();
        fetchKbaseName();
    }, [fetchDocuments, fetchKbaseName]);

    useEffect(() => {
        const raw = normalizeString(embedded ? folderQueryParam : folderNameParam);
        if (!raw) {
            setCurrentFolder('');
            return;
        }

        try {
            setCurrentFolder(decodeURIComponent(raw));
        } catch {
            setCurrentFolder(raw);
        }
    }, [embedded, folderNameParam, folderQueryParam, kbaseId]);

    const fetchCurrentFolderDocument = useCallback(async () => {
        const folderName = normalizeString(currentFolder);
        if (!kbaseId || !folderName) {
            setCurrentFolderDocumentType('');
            setCurrentFolderDocumentStatus('');
            setCurrentFolderMaxUrls(0);
            return;
        }

        try {
            const rootDocuments = await kbaseService.getKbaseDocuments(kbaseId);
            const folderDocument = (Array.isArray(rootDocuments) ? rootDocuments : []).find((row) => (
                normalizeString(row?.folderName || row?.filename || row?.name) === folderName
                && ['folder', 'website'].includes(normalizeString(row?.documentType).toLowerCase())
            ));
            if (!folderDocument && normalizeString(currentFolderDocumentType).toLowerCase() === 'website') {
                setCurrentFolder('');
                setCurrentFolderDocumentType('');
                setCurrentFolderDocumentStatus('');
                setCurrentFolderMaxUrls(0);
                if (embedded) {
                    const params = new URLSearchParams(location.search);
                    params.delete('folder');
                    const query = params.toString();
                    navigate(`${location.pathname}${query ? `?${query}` : ''}`);
                } else {
                    navigate(`/kbases/${encodeURIComponent(kbaseId)}/documents`);
                }
                return;
            }
            setCurrentFolderDocumentType(normalizeString(folderDocument?.documentType));
            setCurrentFolderDocumentStatus(normalizeString(folderDocument?.status));
            setCurrentFolderMaxUrls(Number(folderDocument?.maxUrls) || 0);
        } catch (e) {
            setCurrentFolderDocumentType('');
            setCurrentFolderDocumentStatus('');
            setCurrentFolderMaxUrls(0);
            console.warn('Failed to resolve current folder document', e);
        }
    }, [currentFolder, currentFolderDocumentType, embedded, kbaseId, location.pathname, location.search, navigate]);

    useEffect(() => {
        fetchCurrentFolderDocument();
    }, [fetchCurrentFolderDocument]);

    const navigateToFolder = useCallback(
        (nextFolderName) => {
            if (!kbaseId) return;
            const name = normalizeString(nextFolderName);
            if (!name) {
                setCurrentFolder('');
                if (embedded) {
                    const params = new URLSearchParams(location.search);
                    params.delete('folder');
                    const query = params.toString();
                    navigate(`${location.pathname}${query ? `?${query}` : ''}`);
                } else {
                    navigate(`/kbases/${encodeURIComponent(kbaseId)}/documents`);
                }
                return;
            }

            setCurrentFolder(name);
            if (embedded) {
                const params = new URLSearchParams(location.search);
                params.set('folder', name);
                navigate(`${location.pathname}?${params.toString()}`);
            } else {
                navigate(`/kbases/${encodeURIComponent(kbaseId)}/documents/${encodeURIComponent(name)}`);
            }
        },
        [embedded, kbaseId, location.pathname, location.search, navigate]
    );

    const rows = useMemo(() => {
        const q = normalizeString(documentNameSearch).toLowerCase();
        if (!q) return allRows;

        return allRows.filter((row) => {
            const name = [row?.filename, row?.folderName, row?.name, row?.url]
                .map((value) => normalizeString(value).toLowerCase())
                .filter(Boolean)
                .join(' ');
            return name.includes(q);
        });
    }, [allRows, documentNameSearch]);

    const shouldPollDocuments = useMemo(() => (
        allRows.some((row) => isPollingStatus(row?.status))
        || (
            normalizeString(currentFolderDocumentType).toLowerCase() === 'website'
            && isPollingStatus(currentFolderDocumentStatus)
        )
    ), [allRows, currentFolderDocumentStatus, currentFolderDocumentType]);

    useEffect(() => {
        if (!kbaseId || !shouldPollDocuments) return undefined;

        let inFlight = false;
        const intervalId = window.setInterval(async () => {
            if (inFlight) return;
            inFlight = true;
            try {
                await Promise.all([
                    fetchDocuments({ silent: true }),
                    fetchCurrentFolderDocument(),
                ]);
            } finally {
                inFlight = false;
            }
        }, 2000);

        return () => window.clearInterval(intervalId);
    }, [fetchCurrentFolderDocument, fetchDocuments, kbaseId, shouldPollDocuments]);

    const canCreate = useMemo(() => {
        if (!kbaseId) return false;
        if (!normalizeString(createDraft.documentType)) return false;

        if (createDraft.documentType === 'URL') {
            return getUrlLines(createDraft.url).length > 0;
        }
        if (createDraft.documentType === 'Website') {
            return isValidUrl(createDraft.url) && Boolean(normalizeString(createDraft.folderName));
        }

        // File
        if (!createFile) return false;
        if (createUploading) return false;
        return true;
    }, [createDraft.documentType, createDraft.folderName, createDraft.url, createFile, createUploading, kbaseId]);

    const canCreateFolder = useMemo(() => {
        if (!kbaseId) return false;
        return Boolean(normalizeString(createFolderName));
    }, [createFolderName, kbaseId]);

    const createUrlLines = useMemo(() => getUrlLines(createDraft.url), [createDraft.url]);
    const createValidUrlCount = useMemo(() => createUrlLines.filter(isValidUrl).length, [createUrlLines]);
    const createInvalidUrlCount = createUrlLines.length - createValidUrlCount;

    const createDialogTitle = useMemo(() => {
        if (createDraft.documentType === 'URL') {
            return t('documents.create.addUrls', 'Add URL(s)');
        }
        if (createDraft.documentType === 'Website') return t('documents.create.addWebsite', 'Add Website');

        if (createDraft.ocrRequired) {
            return t('documents.create.addOcrDocument', 'Add OCR Document');
        }

        return t('documents.create.addDocument', 'Add Document');
    }, [createDraft.documentType, createDraft.ocrRequired, t]);

    const onDropFile = useCallback(
        async (acceptedFiles) => {
            const file = acceptedFiles?.[0];
            if (!file) return;

            setCreateFile(file);
            setCreateDraft((prev) => ({ ...prev, filename: file.name, url: '' }));
            setCreateUploadProgress(0);
        },
        []
    );

    const onDropFileRejected = useCallback((fileRejections) => {
        const errors = fileRejections?.flatMap((r) => r.errors || []) || [];
        const tooLarge = errors.some((e) => e.code === 'file-too-large');
        const invalidType = errors.some((e) => e.code === 'file-invalid-type');
        let message = t('documents.upload.rejected', 'This file could not be added.');
        if (tooLarge) {
            message = t('documents.upload.tooLarge', 'File exceeds the 10 MB limit. Please choose a smaller file.');
        } else if (invalidType) {
            message = createDraft.ocrRequired
                ? t('files.ocrPdfOnly', 'Only PDF files are allowed for OCR upload')
                : t('documents.upload.acceptedTypes', 'Only PDF, TXT, DOCX, CSV, and Excel files up to 10 MB are allowed');
        }
        setSnackbar({ open: true, message, severity: 'error' });
    }, [t, createDraft.ocrRequired]);

    // Restrict the file picker / drop to supported types. Extensions are listed in
    // the value arrays so files with a missing/empty MIME (common for CSV on macOS)
    // still match by extension. OCR uploads are PDF-only.
    const fileAccept = createDraft.ocrRequired
        ? { 'application/pdf': ['.pdf'] }
        : {
            'application/pdf': ['.pdf'],
            'text/plain': ['.txt'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'text/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv', '.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
        };

    const { getRootProps: getFileRootProps, getInputProps: getFileInputProps, isDragActive: isFileDragActive } = useDropzone({
        onDrop: onDropFile,
        onDropRejected: onDropFileRejected,
        accept: fileAccept,
        maxSize: MAX_FILE_SIZE,
        multiple: false,
        disabled: createUploading || !createOpen || createDraft.documentType !== 'File',
    });

    const handleCreate = async () => {
        if (!kbaseId) return;
        if (!canCreate) {
            setSnackbar({ open: true, message: t('documents.create.validationError', 'All fields are required'), severity: 'error' });
            return;
        }

        try {
            setLoading(true);
            const documentType = normalizeString(createDraft.documentType);

            let filename = '';
            if (documentType !== 'URL') {
                filename = createFile?.name || normalizeString(createDraft.filename) || 'file';
            }

            if (documentType === 'URL') {
                const urls = getUrlLines(createDraft.url);
                const invalidUrls = urls.filter((url) => !isValidUrl(url));
                if (invalidUrls.length) {
                    setSnackbar({
                        open: true,
                        message: `${t('documents.create.invalidUrls', 'Invalid URL(s)')}: ${invalidUrls.join(', ')}`,
                        severity: 'error',
                    });
                    return;
                }

                const folder = normalizeString(currentFolder) || undefined;
                for (const url of urls) {
                    const payload = {
                        documentType: 'url',
                        filename: inferFilenameFromUrl(url) || 'document',
                        url,
                        refreshRate: normalizeString(createDraft.refreshRate).toLowerCase(),
                        ocrRequired: Boolean(createDraft.ocrRequired),
                        folder,
                    };
                    // eslint-disable-next-line no-await-in-loop
                    await kbaseService.createKbaseDocument(kbaseId, payload, { folder });
                }

                setSnackbar({
                    open: true,
                    message: urls.length > 1
                        ? t('documents.create.multipleSuccess', 'Documents created')
                        : t('documents.create.success', 'Document created'),
                    severity: 'success',
                });
                setCreateOpen(false);
                await fetchDocuments();
                return;
            }

            if (documentType === 'Website') {
                await kbaseService.createKbaseDocument(kbaseId, {
                    documentType: 'website',
                    url: normalizeString(createDraft.url),
                    folderName: normalizeString(createDraft.folderName),
                    crawlDepth: Number(createDraft.crawlDepth),
                    maxUrls: Number(createDraft.maxUrls),
                    folder: normalizeString(currentFolder) || undefined,
                }, { folder: normalizeString(currentFolder) || undefined });
                setSnackbar({ open: true, message: t('documents.create.websiteQueued', 'Website crawl started'), severity: 'success' });
                setCreateOpen(false);
                await fetchDocuments();
                return;
            }

            // File upload: 3-step flow
            if (!createFile) throw new Error('No file selected');

            setCreateUploading(true);
            setCreateUploadProgress(0);

            const presign = await documentService.requestPresign(kbaseId, normalizeString(filename), {
                ocrRequired: Boolean(createDraft.ocrRequired),
                folder: normalizeString(currentFolder) || undefined,
            });
            const presignedUrl = presign?.presignedUrl;
            const documentId = presign?.documentId;
            if (!presignedUrl) throw new Error('presignedUrl not returned');
            if (!documentId) throw new Error('documentId not returned');

            await axios.put(presignedUrl, createFile, {
                headers: { 'Content-Type': createFile.type || 'application/octet-stream' },
                onUploadProgress: (evt) => {
                    const total = evt.total || 0;
                    if (!total) return;
                    const pct = Math.round((evt.loaded * 100) / total);
                    setCreateUploadProgress(pct);
                },
            });

            await documentService.markUploaded(kbaseId, documentId, normalizeString(filename), {
                ocrRequired: Boolean(createDraft.ocrRequired),
                folder: normalizeString(currentFolder) || undefined,
            });

            setSnackbar({ open: true, message: t('documents.upload.success', 'File uploaded'), severity: 'success' });
            setCreateOpen(false);
            await fetchDocuments();
        } catch (e) {
            console.error('Error creating document', e);
            setSnackbar({ open: true, message: e?.message || t('documents.create.error', 'Failed to create document'), severity: 'error' });
        } finally {
            setCreateUploading(false);
            setLoading(false);
        }
    };

    const handleCreateFolder = async () => {
        if (!kbaseId) return;
        if (!canCreateFolder) {
            setSnackbar({ open: true, message: t('documents.folderCreate.validationError', 'Folder name is required'), severity: 'error' });
            return;
        }

        try {
            setLoading(true);
            const folderName = normalizeString(createFolderName);
            const payload = {
                documentType: 'folder',
                folderName,
                ocrRequired: false,
                folder: normalizeString(currentFolder) || undefined,
            };
            await kbaseService.createKbaseDocument(kbaseId, payload, {
                folder: normalizeString(currentFolder) || undefined,
            });
            setSnackbar({ open: true, message: t('documents.folderCreate.success', 'Folder created'), severity: 'success' });
            setCreateFolderOpen(false);
            await fetchDocuments();
        } catch (e) {
            console.error('Error creating folder', e);
            setSnackbar({ open: true, message: e?.message || t('documents.folderCreate.error', 'Failed to create folder'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!kbaseId || !deleteTarget?.documentId) return;

        try {
            setLoading(true);
            const deletingDocument = await kbaseService.deleteKbaseDocument(kbaseId, deleteTarget.documentId);
            setDocuments((current) => current.map((document) => (
                String(document?.documentId) === String(deleteTarget.documentId)
                    ? { ...document, ...deletingDocument, status: 'deleting' }
                    : document
            )));
            setSnackbar({ open: true, message: t('documents.deleteStarted', 'Deletion requested'), severity: 'success' });
            closeDelete();
            await fetchDocuments();
        } catch (e) {
            console.error('Error deleting document', e);
            setSnackbar({ open: true, message: e?.message || t('documents.deleteErrorMsg', 'Failed to delete document'), severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshSave = async () => {
        if (!kbaseId || !refreshDialogDocument?.documentId) return;

        const nextRefreshRate = normalizeString(refreshDialogValue).toLowerCase() || 'never';
        try {
            setUpdatingRefresh(true);
            await kbaseService.updateKbaseDocument(kbaseId, refreshDialogDocument.documentId, {
                documentType: 'url',
                refreshRate: nextRefreshRate,
                url: refreshDialogDocument.url || refreshDialogDocument.filename || refreshDialogDocument.name || '',
            });
            setSnackbar({ open: true, message: t('documents.refreshUpdated', 'Refresh updated'), severity: 'success' });
            setRefreshDialogOpen(false);
            setRefreshDialogDocument(null);
            await fetchDocuments();
        } catch (e) {
            console.error('Error updating refresh rate', e);
            setSnackbar({ open: true, message: e?.message || t('documents.refreshUpdateError', 'Failed to update refresh'), severity: 'error' });
        } finally {
            setUpdatingRefresh(false);
        }
    };

    const handleMoveSave = async () => {
        if (!kbaseId || !moveDialogDocument?.documentId) return;

        const sourceFolder = normalizeString(moveDialogDocument?.folder) || normalizeString(currentFolder);
        const targetFolder = moveDialogFolder === MOVE_ROOT_VALUE ? '' : normalizeString(moveDialogFolder);
        if (sourceFolder === targetFolder) {
            setSnackbar({ open: true, message: t('documents.moveSameFolder', 'Choose a different folder'), severity: 'error' });
            return;
        }

        try {
            setMovingDocument(true);
            await kbaseService.moveKbaseDocument(kbaseId, moveDialogDocument.documentId, targetFolder);
            setSnackbar({ open: true, message: t('documents.moveSuccess', 'Document moved'), severity: 'success' });
            setMoveDialogOpen(false);
            setMoveDialogDocument(null);
            setMoveDialogFolder('');
            setMoveFolderOptions([]);
            await fetchDocuments();
        } catch (e) {
            console.error('Error moving document', e);
            setSnackbar({ open: true, message: e?.message || t('documents.moveError', 'Failed to move document'), severity: 'error' });
        } finally {
            setMovingDocument(false);
        }
    };

    const moveSourceFolder = normalizeString(moveDialogDocument?.folder) || normalizeString(currentFolder);
    const moveTargetFolder = moveDialogFolder === MOVE_ROOT_VALUE ? '' : normalizeString(moveDialogFolder);
    const isWebsiteFolder = normalizeString(currentFolderDocumentType).toLowerCase() === 'website';
    const websiteDocumentCounts = allRows.reduce((counts, row) => {
        const documentType = normalizeString(row?.documentType).toLowerCase();
        const status = normalizeString(row?.status).toLowerCase();
        const linkType = normalizeString(row?.linkType).toLowerCase();

        if (documentType === 'url') {
            counts.total += 1;
            if (status === 'created' || status === 'queued') counts.queued += 1;
            if (status === 'processing') counts.processing += 1;
            if (status === 'processed' || status === 'success') counts.processed += 1;
            if (status === 'failed') counts.failed += 1;
        }
        if (linkType === 'int') counts.int += 1;
        if (linkType === 'ext') counts.ext += 1;
        if (isPdfDocument(row)) counts.pdf += 1;
        return counts;
    }, { total: 0, queued: 0, processing: 0, processed: 0, failed: 0, int: 0, ext: 0, pdf: 0 });
    const websiteCompletedCount = websiteDocumentCounts.processed + websiteDocumentCounts.failed;
    const websiteProgressPercent = currentFolderMaxUrls > 0
        ? Math.min(100, (websiteCompletedCount / currentFolderMaxUrls) * 100)
        : 0;

    const currentFolderIndicator = normalizeString(currentFolder) ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
                {t('documents.currentFolder', 'Current folder')}: {normalizeString(currentFolder)}
            </Typography>
            <Tooltip title={t('documents.currentFolderClear', 'Go to root')} placement="top" arrow>
                <span>
                    <IconButton
                        size="small"
                        onClick={() => navigateToFolder('')}
                        disabled={loading || !kbaseId}
                        sx={{
                            width: 30,
                            height: 30,
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            bgcolor: 'primary.50',
                            '&:hover': {
                                bgcolor: 'primary.100',
                                borderColor: 'primary.dark',
                            },
                        }}
                    >
                        <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    ) : null;

    const websiteCrawlSummary = isWebsiteFolder ? (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 0.75,
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t('documents.website.crawlStatus', 'Website crawl')}: {statusLabel(currentFolderDocumentStatus || 'created')}
            </Typography>
            {[
                ['total', t('documents.website.totalUrls', 'Total URLs'), websiteDocumentCounts.total, '#475467'],
                ['queued', t('documents.website.queued', 'Queued'), websiteDocumentCounts.queued, '#7a5af8'],
                ['processing', t('documents.website.processing', 'Processing'), websiteDocumentCounts.processing, '#287bd6'],
                ['processed', t('documents.website.processed', 'Processed'), websiteDocumentCounts.processed, '#067647'],
                ['failed', t('documents.website.failed', 'Failed'), websiteDocumentCounts.failed, '#b42318'],
                ['int', 'INT', websiteDocumentCounts.int, '#287bd6'],
                ['ext', 'EXT', websiteDocumentCounts.ext, '#b26a00'],
                ['pdf', 'PDF', websiteDocumentCounts.pdf, '#b42318'],
            ].map(([key, label, count, color]) => (
                <Box
                    key={key}
                    component="span"
                    sx={{
                        px: 0.8,
                        py: 0.2,
                        borderRadius: 10,
                        bgcolor: `${color}14`,
                        color,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        lineHeight: 1.5,
                    }}
                >
                    {label}: {count}
                </Box>
            ))}
            {websiteProgressPercent < 100 && (
                <Box sx={{ width: '100%', pt: 0.25 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.4 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                            {t('documents.website.progress', 'Progress')}: {websiteCompletedCount} / {currentFolderMaxUrls || 0}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                            {Math.round(websiteProgressPercent)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={websiteProgressPercent}
                        aria-label={t('documents.website.progress', 'Website processing progress')}
                        sx={{ height: 7, borderRadius: 4 }}
                    />
                </Box>
            )}
        </Box>
    ) : null;

    return (
        <Box sx={{ p: embedded ? 0 : 2, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            {!embedded && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/kbases')}
                        disabled={loading}
                        sx={{ textTransform: 'none', borderRadius: 1.5, flexShrink: 0 }}
                    >
                        {t('documents.back', 'Back to Kbases')}
                    </Button>
                    <Typography variant="h5" fontWeight="bold" noWrap>
                        {t('documents.title', 'Documents')}{kbaseName ? `: ${kbaseName}` : kbaseId ? `: ${kbaseId}` : ''}
                    </Typography>
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    mb: embedded ? 1.5 : 2,
                }}
            >
                {toolbarStart ? (
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        {toolbarStart}
                    </Box>
                ) : null}
                {currentFolderIndicator}
                {websiteCrawlSummary}
                <TextField
                    type="search"
                    size="small"
                    value={documentNameSearch}
                    onChange={(e) => setDocumentNameSearch(e.target.value)}
                    placeholder={t('documents.searchByName', 'Search document names')}
                    disabled={loading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        flex: '1 1 300px',
                        minWidth: { xs: '100%', sm: 240 },
                        maxWidth: { sm: 440 },
                        '& .MuiOutlinedInput-root': {
                            height: TOOLBAR_CONTROL_HEIGHT,
                            borderRadius: 1.5,
                            bgcolor: 'background.paper',
                        },
                    }}
                />
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                        ml: { sm: 'auto' },
                    }}
                >
                    <Tooltip title={t('documents.refresh', 'Refresh')} placement="top" arrow>
                        <span>
                            <IconButton
                                onClick={fetchDocuments}
                                disabled={loading || !kbaseId}
                                aria-label={t('documents.refresh', 'Refresh')}
                                sx={{
                                    width: TOOLBAR_CONTROL_HEIGHT,
                                    height: TOOLBAR_CONTROL_HEIGHT,
                                    borderRadius: 1.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    color: 'text.secondary',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        color: 'primary.main',
                                        bgcolor: 'action.hover',
                                    },
                                }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {!normalizeString(currentFolder) ? (
                        <Button
                            variant="outlined"
                            startIcon={<CreateNewFolderIcon />}
                            onClick={openCreateFolder}
                            disabled={loading || !kbaseId}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 1.5,
                                height: TOOLBAR_CONTROL_HEIGHT,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t('documents.newFolder', 'New Folder')}
                        </Button>
                    ) : null}
                    <Button
                        variant="contained"
                        disableElevation
                        startIcon={<AddIcon />}
                        onClick={openCreateMenu}
                        disabled={loading || !kbaseId || isWebsiteFolder}
                        aria-controls={createMenuAnchorEl ? 'documents-create-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={createMenuAnchorEl ? 'true' : undefined}
                        sx={{
                            textTransform: 'none',
                            borderRadius: 1.5,
                            height: TOOLBAR_CONTROL_HEIGHT,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {t('documents.new', 'New Document')}
                    </Button>
                </Box>
                <Menu
                    id="documents-create-menu"
                    anchorEl={createMenuAnchorEl}
                    open={Boolean(createMenuAnchorEl)}
                    onClose={closeCreateMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        elevation: 8,
                        sx: {
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'background.paper',
                            boxShadow: '0 14px 34px rgba(15, 23, 42, 0.18)',
                            overflow: 'hidden',
                            '& .MuiMenu-list': {
                                py: 0.75,
                            },
                            '& .MuiMenuItem-root': {
                                px: 2.5,
                                py: 1.35,
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: 'text.primary',
                                '&:hover': {
                                    bgcolor: 'primary.light',
                                    color: 'primary.contrastText',
                                },
                            },
                        },
                    }}
                >
                    <MenuItem onClick={() => openCreate({ documentType: 'File', ocrRequired: false })}>
                        {t('documents.create.addDocument', 'Add Document')}
                    </MenuItem>
                    <MenuItem onClick={() => openCreate({ documentType: 'File', ocrRequired: true })}>
                        {t('documents.create.addOcrDocument', 'Add OCR Document')}
                    </MenuItem>
                    <MenuItem onClick={() => openCreate({ documentType: 'URL', ocrRequired: false })}>
                        {t('documents.create.addUrls', 'Add URL(s)')}
                    </MenuItem>
                    <MenuItem onClick={() => openCreate({ documentType: 'Website', ocrRequired: false })}>
                        {t('documents.create.addWebsite', 'Add Website')}
                    </MenuItem>
                </Menu>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ mt: embedded ? 0 : 2, borderRadius: 2, width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 980 }}>
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'grey.200', whiteSpace: 'nowrap' } }}>
                            <TableCell sx={{ width: 'auto' }}>{t('documents.name', 'Document Name')}</TableCell>
                            <TableCell sx={{ width: 64 }}>{t('documents.documentType', 'Type')}</TableCell>
                            <TableCell sx={{ width: 64 }}>{t('documents.fileSize', 'Size')}</TableCell>
                            <TableCell sx={{ width: 128 }}>{t('documents.processedAt', 'Last Processed')}</TableCell>
                            <TableCell sx={{ width: 88 }}>{t('documents.refreshRate', 'Refresh')}</TableCell>
                            <TableCell sx={{ width: 132 }}>{t('documents.status', 'Status')}</TableCell>
                            <TableCell sx={{ width: 124 }}>{t('documents.actions', 'Actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const processedMs = normalizeEpochToMs(row.processedAt);
                            const processedLabel = processedMs ? new Date(processedMs).toLocaleDateString() : '-';

                            const documentType = String(row.documentType || '').trim();
                            const documentTypeLower = documentType.toLowerCase();
                            const isUrl = documentTypeLower === 'url';
                            const isFolder = documentTypeLower === 'folder';
                            const isWebsite = documentTypeLower === 'website';
                            const isDeleting = normalizeString(row.status).toLowerCase() === 'deleting';
                            const folderNameValue = row.folderName || row.filename || row.name || '-';
                            const nameValue = isUrl ? (row.url || '-') : (row.filename || row.folderName || row.name || '-');

                            return (
                                <TableRow key={String(row.documentId || row.filename || Math.random())} hover sx={{ '& td': { verticalAlign: 'top' } }}>
                                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'normal', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                                        {isUrl && row.url ? (
                                            <Link
                                                href={row.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                underline="hover"
                                                onMouseEnter={(event) => openUrlPreview(event, row)}
                                                onMouseLeave={closeUrlPreview}
                                                onFocus={(event) => openUrlPreview(event, row)}
                                                onBlur={closeUrlPreview}
                                            >
                                                {formatUrlForDisplay(row.url)}
                                            </Link>
                                        ) : (isFolder || isWebsite) ? (
                                            <Box
                                                sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, cursor: 'pointer', minWidth: 0 }}
                                                onDoubleClick={() => navigateToFolder(String(folderNameValue || '').trim())}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') navigateToFolder(String(folderNameValue || '').trim());
                                                }}
                                            >
                                                <FolderIcon fontSize="small" color="action" sx={{ flexShrink: 0, mt: 0.25 }} />
                                                <Typography variant="body2" component="span">{folderNameValue}</Typography>
                                            </Box>
                                        ) : isPdfDocument(row) ? (
                                            <Box
                                                component="button"
                                                type="button"
                                                onClick={() => openPdfViewer(row)}
                                                onMouseEnter={(event) => openUrlPreview(event, row)}
                                                onMouseLeave={closeUrlPreview}
                                                onFocus={(event) => openUrlPreview(event, row)}
                                                onBlur={closeUrlPreview}
                                                sx={{ display: 'inline', p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', font: 'inherit', textAlign: 'left', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {nameValue}
                                            </Box>
                                        ) : (
                                            nameValue
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {typeLabel(row.documentType)}
                                        {isUrl && row.linkType ? (
                                            <Typography component="span" sx={{ ml: 0.5, fontSize: '0.68rem', fontWeight: 700, color: row.linkType === 'ext' ? '#b26a00' : '#287bd6' }}>
                                                {String(row.linkType).toUpperCase()}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{(isFolder || isWebsite) ? '-' : formatBytes(row.fileSize)}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{processedLabel}</TableCell>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {isUrl ? (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => openRefreshDialog(row)}
                                                disabled={loading || isDeleting || !row?.documentId}
                                                sx={{ minWidth: 0, p: 0, textTransform: 'none' }}
                                            >
                                                {refreshLabel(row.refreshRate || 'never')}
                                            </Button>
                                        ) : (
                                            '-'
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ width: 132, maxWidth: 132 }}>
                                        <Typography
                                            variant="body2"
                                            title={statusLabel(row.status)}
                                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        >
                                            {statusLabel(row.status)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                                            <Tooltip title={t('documents.viewChunks', 'View Chunks')} placement="top" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openChunks(row)}
                                                        disabled={loading || isDeleting || isFolder || !row?.documentId}
                                                    >
                                                        <ViewListIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t('documents.move', 'Move')} placement="top" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openMoveDialog(row)}
                                                        disabled={loading || isDeleting || isFolder || !row?.documentId}
                                                    >
                                                        <DriveFileMoveIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={t('documents.delete', 'Delete')} placement="top" arrow>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => openDelete(row)}
                                                        disabled={loading || isDeleting || !row?.documentId}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('documents.empty', 'No documents found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Popper
                open={Boolean(urlPreview.anchorEl)}
                anchorEl={urlPreview.anchorEl}
                placement="top-start"
                modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                sx={{ zIndex: (theme) => theme.zIndex.tooltip, pointerEvents: 'none' }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: 560,
                        maxWidth: 'calc(100vw - 24px)',
                        overflow: 'hidden',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                        boxShadow: '0 30px 80px rgba(15, 23, 42, 0.34), 0 10px 28px rgba(15, 23, 42, 0.24)',
                    }}
                >
                    {urlPreview.loading ? (
                        <Box sx={{ height: 350, display: 'grid', placeItems: 'center', bgcolor: 'grey.100' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : urlPreview.error ? (
                        <Box sx={{ height: 220, display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
                            {t('documents.urlPreviewUnavailable', 'Preview unavailable')}
                        </Box>
                    ) : (
                        <Box
                            component="img"
                            src={urlPreview.imageUrl}
                            alt={t('documents.urlPreview', 'URL preview')}
                            sx={{ display: 'block', width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', objectPosition: 'top', bgcolor: 'grey.100' }}
                        />
                    )}
                    <Typography variant="caption" component="div" noWrap sx={{ px: 1.5, py: 1, color: 'text.secondary' }}>
                        {urlPreview.url}
                    </Typography>
                </Paper>
            </Popper>

            <Dialog open={pdfViewer.open} onClose={closePdfViewer} fullWidth maxWidth="lg" PaperProps={{ sx: { height: '92vh' } }}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography component="span" variant="h6" noWrap>{pdfViewer.filename}</Typography>
                    <IconButton onClick={closePdfViewer} aria-label={t('common.close', 'Close')}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 0, display: 'grid', minHeight: 0, bgcolor: 'grey.100' }}>
                    {pdfViewer.loading ? (
                        <Box sx={{ display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
                    ) : pdfViewer.error ? (
                        <Box sx={{ display: 'grid', placeItems: 'center', color: 'text.secondary' }}>
                            {t('documents.pdfPreviewUnavailable', 'PDF preview unavailable')}
                        </Box>
                    ) : (
                        <Box component="iframe" src={pdfViewer.src} title={pdfViewer.filename} sx={{ width: '100%', height: '100%', border: 0, bgcolor: '#fff' }} />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={refreshDialogOpen} onClose={closeRefreshDialog} fullWidth maxWidth="xs">
                <DialogTitle>{t('documents.modifyRefresh', 'Modify Refresh Rate')}</DialogTitle>
                <DialogContent>
                    {refreshDialogDocument ? (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 1,
                                wordBreak: 'break-all',
                                color: 'text.secondary',
                            }}
                        >
                            {formatUrlForDisplay(refreshDialogDocument.url || refreshDialogDocument.filename || refreshDialogDocument.name)}
                        </Typography>
                    ) : null}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('documents.refreshRate', 'Refresh')}
                        value={refreshDialogValue}
                        onChange={(e) => setRefreshDialogValue(e.target.value)}
                        sx={{ mt: 1 }}
                    >
                        <MenuItem value="never">{t('documents.refreshNever', 'Never')}</MenuItem>
                        <MenuItem value="daily">{t('documents.refreshDaily', 'Daily')}</MenuItem>
                        <MenuItem value="weekly">{t('documents.refreshWeekly', 'Weekly')}</MenuItem>
                        <MenuItem value="monthly">{t('documents.refreshMonthly', 'Monthly')}</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRefreshDialog} disabled={updatingRefresh}>
                        {t('documents.cancel', 'Cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleRefreshSave} disabled={updatingRefresh || !refreshDialogDocument?.documentId}>
                        {updatingRefresh ? t('documents.saving', 'Saving...') : t('documents.save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={moveDialogOpen} onClose={closeMoveDialog} fullWidth maxWidth="xs">
                <DialogTitle>{t('documents.moveTitle', 'Move Document')}</DialogTitle>
                <DialogContent dividers>
                    {moveDialogDocument ? (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                wordBreak: 'break-all',
                                color: 'text.secondary',
                            }}
                        >
                            {getDocumentDisplayName(moveDialogDocument)}
                        </Typography>
                    ) : null}
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('documents.moveDestination', 'Destination')}
                        value={moveDialogFolder}
                        onChange={(e) => setMoveDialogFolder(e.target.value)}
                    >
                        {moveSourceFolder ? (
                            <MenuItem value={MOVE_ROOT_VALUE}>{t('documents.moveRoot', 'Root')}</MenuItem>
                        ) : null}
                        {moveFolderOptions.map((folder) => (
                            <MenuItem key={folder} value={folder}>
                                {folder}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeMoveDialog} disabled={movingDocument} sx={{ textTransform: 'none' }}>
                        {t('documents.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleMoveSave}
                        disabled={
                            movingDocument
                            || !moveDialogDocument?.documentId
                            || moveSourceFolder === moveTargetFolder
                        }
                        sx={{ textTransform: 'none' }}
                    >
                        {movingDocument ? t('documents.moving', 'Moving...') : t('documents.move', 'Move')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
                <ModalHeader
                    icon={
                        createDraft.documentType === 'URL' || createDraft.documentType === 'Website'
                            ? <LinkIcon />
                            : createDraft.ocrRequired
                                ? <DocumentScannerOutlinedIcon />
                                : <DescriptionOutlinedIcon />
                    }
                    title={createDialogTitle}
                    subtitle={
                        createDraft.documentType === 'URL'
                            ? t('documents.create.addUrlsSubtitle', 'Import content from web pages into this knowledge base')
                            : createDraft.documentType === 'Website'
                                ? t('documents.create.addWebsiteSubtitle', 'Crawl internal pages and group discovered links in one folder')
                            : createDraft.ocrRequired
                                ? t('documents.create.addOcrDocumentSubtitle', 'Extract text from a scanned PDF with OCR')
                                : t('documents.create.addDocumentSubtitle', 'Upload a document to this knowledge base')
                    }
                    onClose={closeCreate}
                    disabled={loading}
                />
                <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
                    {normalizeString(currentFolder) ? (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                mb: 1.75,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '8px',
                                backgroundColor: 'rgba(40,123,214,0.08)',
                            }}
                        >
                            <FolderIcon sx={{ fontSize: 16, color: BRAND }} />
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: BRAND_DARK }}>
                                {normalizeString(currentFolder)}
                            </Typography>
                        </Box>
                    ) : null}

                    {createDraft.documentType === 'Website' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                type="url"
                                label={t('documents.website.startUrl', 'Website URL')}
                                value={createDraft.url}
                                onChange={(e) => setCreateDraft((prev) => ({ ...prev, url: e.target.value }))}
                                placeholder="https://example.com"
                            />
                            <TextField
                                fullWidth
                                size="small"
                                label={t('documents.website.folderName', 'Website folder name')}
                                value={createDraft.folderName}
                                onChange={(e) => setCreateDraft((prev) => ({ ...prev, folderName: e.target.value }))}
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField
                                    size="small"
                                    type="number"
                                    label={t('documents.website.depth', 'Crawl depth')}
                                    value={createDraft.crawlDepth}
                                    onChange={(e) => setCreateDraft((prev) => ({ ...prev, crawlDepth: e.target.value }))}
                                    inputProps={{ min: 0, max: 10 }}
                                    helperText={t('documents.website.depthHelper', 'Default and maximum: 10 levels')}
                                />
                                <TextField
                                    size="small"
                                    type="number"
                                    label={t('documents.website.maxUrls', 'Maximum URLs')}
                                    value={createDraft.maxUrls}
                                    onChange={(e) => setCreateDraft((prev) => ({ ...prev, maxUrls: e.target.value }))}
                                    inputProps={{ min: 1, max: 200 }}
                                    helperText={t('documents.website.maxUrlsHelper', 'Default and maximum: 200 URLs')}
                                />
                            </Box>
                            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                {t('documents.website.linkHint', 'Internal links are crawled and labeled INT. External links are fetched and labeled EXT, but links within them are not crawled.')}
                            </Typography>
                        </Box>
                    ) : createDraft.documentType === 'URL' ? (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#2b3a4b' }}>
                                    {t('documents.url', 'URL')}
                                </Typography>
                                {createUrlLines.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                                        <Box
                                            component="span"
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 22, px: 1, borderRadius: '11px', fontSize: '0.72rem', fontWeight: 600, color: '#2f9e6f', backgroundColor: 'rgba(47,158,111,0.12)' }}
                                        >
                                            <CheckCircleIcon sx={{ fontSize: 15 }} />
                                            {t('documents.create.urlValidCount', { count: createValidUrlCount, defaultValue: `${createValidUrlCount} valid` })}
                                        </Box>
                                        {createInvalidUrlCount > 0 && (
                                            <Box
                                                component="span"
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, height: 22, px: 1, borderRadius: '11px', fontSize: '0.72rem', fontWeight: 600, color: '#c0392b', backgroundColor: 'rgba(224,85,78,0.12)' }}
                                            >
                                                <ErrorOutlineIcon sx={{ fontSize: 15 }} />
                                                {t('documents.create.urlInvalidCount', { count: createInvalidUrlCount, defaultValue: `${createInvalidUrlCount} invalid` })}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Excel-like editable URL grid */}
                            <Box sx={{ border: '1px solid #d8e0ea', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '44px 1fr 44px',
                                        backgroundColor: '#f2f5f9',
                                        borderBottom: '1px solid #e2e8f0',
                                        '& > *': { py: 0.85, fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' },
                                    }}
                                >
                                    <Box sx={{ textAlign: 'center', borderRight: '1px solid #e2e8f0' }}>#</Box>
                                    <Box sx={{ px: 1.5 }}>{t('documents.url', 'URL')}</Box>
                                    <Box />
                                </Box>
                                <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                                    {urlRows.map((row, i) => {
                                        const v = row.value.trim();
                                        const status = v === '' ? 'empty' : isValidUrl(v) ? 'valid' : 'invalid';
                                        const isLast = i === urlRows.length - 1;
                                        return (
                                            <Box
                                                key={row.id}
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '44px 1fr 44px',
                                                    borderBottom: isLast ? 'none' : '1px solid #eef2f6',
                                                    backgroundColor: status === 'invalid' ? 'rgba(224,85,78,0.05)' : 'transparent',
                                                    transition: 'background-color .15s ease',
                                                    '&:hover': { backgroundColor: status === 'invalid' ? 'rgba(224,85,78,0.09)' : '#f7f9fc' },
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #eef2f6', backgroundColor: '#fafbfd', fontSize: '0.75rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                                                    {i + 1}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5 }}>
                                                    <InputBase
                                                        inputRef={(el) => {
                                                            if (el) urlRowRefs.current[row.id] = el;
                                                            else delete urlRowRefs.current[row.id];
                                                        }}
                                                        value={row.value}
                                                        onChange={(e) => handleUrlRowChange(row.id, e.target.value)}
                                                        onKeyDown={(e) => handleUrlRowKeyDown(row.id, e)}
                                                        onPaste={(e) => handleUrlRowPaste(row.id, e)}
                                                        placeholder={i === 0 ? 'https://example.com/page' : ''}
                                                        fullWidth
                                                        spellCheck={false}
                                                        autoComplete="off"
                                                        inputProps={{ 'aria-label': `${t('documents.url', 'URL')} ${i + 1}`, type: 'url' }}
                                                        sx={{ fontFamily: 'monospace', fontSize: '0.85rem', py: 0.9, color: status === 'invalid' ? '#c0392b' : '#2b3a4b', '& input::placeholder': { color: '#9aa7b5', opacity: 1 } }}
                                                    />
                                                    {status === 'valid' && <CheckCircleIcon sx={{ fontSize: 16, color: '#2f9e6f', flexShrink: 0 }} />}
                                                    {status === 'invalid' && (
                                                        <Tooltip title={t('documents.create.invalidUrls', 'Invalid URL(s)')}>
                                                            <ErrorOutlineIcon sx={{ fontSize: 16, color: '#e0554e', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', borderLeft: '1px solid #eef2f6' }}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t('documents.upload.remove', 'Remove')}
                                                        onClick={() => handleUrlRowRemove(row.id)}
                                                        disabled={urlRows.length === 1 && row.value === ''}
                                                        sx={{ color: 'text.secondary', '&:hover': { color: '#e0554e' } }}
                                                    >
                                                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
                                <Button
                                    onClick={() => handleUrlRowAdd()}
                                    startIcon={<AddIcon />}
                                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', color: BRAND, borderRadius: '8px' }}
                                >
                                    {t('documents.create.addUrlRow', 'Add URL')}
                                </Button>
                                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {t('documents.create.urlGridHint', 'Paste multiple lines to add rows · Enter for a new row')}
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={t('documents.refreshRate', 'Refresh Rate')}
                                    value={createDraft.refreshRate}
                                    onChange={(e) => setCreateDraft((prev) => ({ ...prev, refreshRate: e.target.value }))}
                                    helperText={t('documents.create.refreshHelper', 'How often we re-fetch content from these URLs.')}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                                >
                                    <MenuItem value="None">{t('documents.refreshNever', 'Never')}</MenuItem>
                                    <MenuItem value="Daily">{t('documents.refreshDaily', 'Daily')}</MenuItem>
                                    <MenuItem value="Weekly">{t('documents.refreshWeekly', 'Weekly')}</MenuItem>
                                    <MenuItem value="Monthly">{t('documents.refreshMonthly', 'Monthly')}</MenuItem>
                                </TextField>
                            </Box>
                        </>
                    ) : (
                        <>
                            <Box
                                {...getFileRootProps()}
                                sx={{
                                    width: '100%',
                                    minHeight: 200,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 0.75,
                                    p: '28px 20px',
                                    textAlign: 'center',
                                    cursor: createUploading ? 'default' : 'pointer',
                                    border: `2px dashed ${isFileDragActive ? BRAND : '#cdd7e3'}`,
                                    borderRadius: '14px',
                                    backgroundColor: isFileDragActive ? 'rgba(40,123,214,0.06)' : '#f7f9fc',
                                    transition: 'border-color .2s ease, background-color .2s ease',
                                    '&:hover': createUploading ? {} : { borderColor: BRAND, backgroundColor: 'rgba(40,123,214,0.04)' },
                                }}
                            >
                                <input {...getFileInputProps()} />
                                <Box
                                    aria-hidden
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: isFileDragActive ? 'rgba(40,123,214,0.14)' : 'rgba(40,123,214,0.08)',
                                        color: BRAND,
                                        mb: 0.5,
                                    }}
                                >
                                    <CloudUploadOutlinedIcon sx={{ fontSize: 32 }} />
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#2b3a4b', whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                                    {isFileDragActive
                                        ? t('files.drop', 'Drop Files Here...')
                                        : createDraft.ocrRequired
                                            ? t('documents.upload.dragDropOcr', 'Drag & drop PDF files here, or select from your computer (max 10 MB)')
                                            : t('documents.upload.dragDrop', 'Drag & drop PDF, TEXT, DOCX, CSV, or Excel files here, or select from your computer (max 10 MB)')}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary', fontSize: '0.8rem' }}>
                                    {createDraft.ocrRequired
                                        ? t('files.ocrPdfOnly', 'Only PDF files are allowed for OCR upload')
                                        : t('documents.upload.acceptedTypes', 'Only PDF, TXT, DOCX, CSV, and Excel files up to 10 MB are allowed')}
                                </Typography>
                            </Box>

                            {createFile && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        p: 1.25,
                                        borderRadius: '10px',
                                        border: '1px solid #e6ebf1',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <Box sx={{ flexShrink: 0, display: 'flex' }}>{fileIconFor(createFile.name)}</Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.86rem', fontWeight: 600, color: '#2b3a4b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {createFile.name}
                                        </Typography>
                                        {createUploading ? (
                                            <LinearProgress
                                                variant="determinate"
                                                value={createUploadProgress}
                                                sx={{ mt: 0.75, height: 5, borderRadius: 3, backgroundColor: '#eef2f6', '& .MuiLinearProgress-bar': { backgroundColor: BRAND } }}
                                            />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', mt: 0.1 }}>
                                                {formatBytes(createFile.size)}
                                            </Typography>
                                        )}
                                    </Box>
                                    {createUploading ? (
                                        createUploadProgress >= 100 ? (
                                            <CheckCircleIcon sx={{ color: '#2f9e6f', fontSize: 20 }} />
                                        ) : (
                                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: BRAND, minWidth: 34, textAlign: 'right' }}>
                                                {createUploadProgress}%
                                            </Typography>
                                        )
                                    ) : (
                                        <Tooltip title={t('documents.upload.remove', 'Remove')}>
                                            <IconButton
                                                size="small"
                                                aria-label={t('documents.upload.remove', 'Remove')}
                                                onClick={() => { setCreateFile(null); setCreateDraft((prev) => ({ ...prev, filename: '' })); setCreateUploadProgress(0); }}
                                                sx={{ color: 'text.secondary', '&:hover': { color: '#e0554e' } }}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
                    <Button onClick={closeCreate} disabled={loading} sx={cancelBtnSx}>
                        {t('documents.create.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleCreate} disabled={loading || !canCreate || (createDraft.documentType === 'URL' && createInvalidUrlCount > 0)} sx={primaryBtnSx}>
                        {createUploading ? t('documents.upload.progress', 'Uploading') : t('documents.create.save', 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={createFolderOpen} onClose={closeCreateFolder} maxWidth="xs" fullWidth>
                <DialogTitle>{t('documents.folderCreate.title', 'Create Folder')}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
                        <TextField
                            label={t('documents.folderName', 'Folder Name')}
                            value={createFolderName}
                            onChange={(e) => setCreateFolderName(e.target.value)}
                            autoFocus
                            fullWidth
                            size="small"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreateFolder} disabled={loading} sx={{ textTransform: 'none' }}>
                        {t('documents.create.cancel', 'Cancel')}
                    </Button>
                    <Button variant="contained" onClick={handleCreateFolder} disabled={loading || !canCreateFolder} sx={{ textTransform: 'none' }}>
                        {t('documents.create.save', 'Create')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmDeleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth>
                <DialogTitle>{t('documents.deleteTitle', 'Delete Document')}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        {t('documents.deleteConfirm', 'Are you sure you want to delete this document?')}
                    </Typography>
                    {deleteTarget && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2">
                                {String(deleteTarget.documentType || '').trim().toLowerCase() === 'url'
                                    ? deleteTarget.url || '-'
                                    : deleteTarget.filename || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {deleteTarget.documentId ? `ID: ${deleteTarget.documentId}` : ''}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDelete} disabled={loading} sx={{ textTransform: 'none' }}>
                        {t('documents.cancel', 'Cancel')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={loading || !deleteTarget?.documentId}
                        sx={{ textTransform: 'none' }}
                    >
                        {t('documents.delete', 'Delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ChunkListDialog
                open={chunksDialogOpen}
                onClose={closeChunks}
                kbaseId={kbaseId}
                documentId={selectedChunkDocument?.documentId}
                documentName={
                    String(selectedChunkDocument?.documentType || '').trim().toLowerCase() === 'url'
                        ? selectedChunkDocument?.url
                        : selectedChunkDocument?.filename
                }
            />
        </Box>
    );
}

export default KbaseDocuments;
